import React, { useEffect, useState } from "react";
import { Grid, Typography, Button, MenuItem, FormControl, Select, Card, Modal, FormGroup, Slide, Backdrop } from "@material-ui/core";
import { Header, PefaCard, LoadingIndicator } from "../../components";
import { RefreshIcon, Wave } from "../../assets/images/";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { inject, observer } from "mobx-react";
import { Root } from "../../stores";
import { filteredPeriodKeys } from "../../stores/pefaStore";
import { useAuth } from "oidc-react";
import { useHistory } from "react-router-dom";
import { ASSESSMENTPREVIEW, INFORMEDCONSENT, MEDICALDETAILS, REFERRALDETAILS } from "../../routes/paths";
import axios from "axios";
import { tokenInterceptor } from "../../services/auth/tokenInterceptor";
import { dataStorePageKeys, dataStorePathKeysFriendly } from "../../stores/dataStore";

interface DashboardProps {
  rootStore?: Root;
}

interface DashboardState {
  isInitialised: boolean;
  isLoading: boolean;
  periodKey: filteredPeriodKeys;
  pefaCount: number;
  name: string;
  pefaDateKeys: string[];
  pefaListingCurrent: boolean;
  groupedPefas: any;
  selectedPefaId: string;
  isLoadLastPage: boolean;
  menuPages: Snapshot[];
  selectedLastPage: string;
  selectedSysEndTime: string;
  lastPage: string;
  isLoadBookingNote: boolean;
  bookingNote: string;
}
export interface Snapshot {
  bookingId: number;
  sysEndTime: string;
  timestamp: string;
  last_Page: string;
}

const Dashboard = inject("rootStore")(
  observer((props: DashboardProps) => {
    const auth = useAuth();
    const history = useHistory();
    tokenInterceptor(axios, null, auth);

    const [state, setState] = useState<DashboardState>({
      isInitialised: false,
      isLoading: true,
      periodKey: filteredPeriodKeys.TODAY,
      pefaCount: props.rootStore ? props.rootStore?.pefaStore.pefaCount : 0,
      name: props.rootStore ? props.rootStore.userStore.getUserNameFormatted : "",
      pefaDateKeys: [],
      pefaListingCurrent: true,
      groupedPefas: {},
      selectedPefaId: "",
      isLoadLastPage: false,
      menuPages: [],
      selectedLastPage: "",
      selectedSysEndTime: "",
      lastPage: "",
      isLoadBookingNote: false,
      bookingNote: "",
    });

    // check we have a valid user or else try to sign in again
    if (!props.rootStore?.userStore.userDetails) {
      auth.signIn();
    }

    const isChrome = () => {
      // disabled: stop reminding people about Chrome
      // const agent = window.navigator.userAgent.toLowerCase();
      // if (agent.indexOf("edg") > -1) {
      //   return false;
      // }
      // if (agent.indexOf("opr") > -1) {
      //   return false;
      // }
      // return agent.indexOf("chrome") > -1 && !!window["chrome"];
      return true;
    };

    const [totalPefas, setTotalPefas] = useState<number>(0);

    const handlePeriodChange = async (event: React.ChangeEvent<{ value: unknown }>): Promise<void> => {
      const periodKey = event.target.value as filteredPeriodKeys;
      const groupedPefas = filterPefas(periodKey);

      await setState((prevState) => ({
        ...prevState,
        periodKey,
        pefaDateKeys: Object.keys(groupedPefas),
        groupedPefas,
      }));
    };
    const handleRefreshPefas = async (): Promise<void> => {
      const { rootStore } = props;

      if (rootStore) {
        setState((prevState) => ({
          ...prevState,
          isLoading: true,
          pefaDateKeys: [],
        }));

        await fetchPefaHandler(true);
      }
    };

    const filterPefas = (periodKey: filteredPeriodKeys): any => {
      const { rootStore } = props;

      if (rootStore) {
        const groupedPefas = rootStore.pefaStore.filteredPefas(periodKey, state.pefaListingCurrent);

        let pefaCount = 0;
        const pefaKeys = Object.keys(groupedPefas);
        pefaKeys.forEach((pefaKey) => {
          pefaCount += groupedPefas[pefaKey].length;
        });

        setTotalPefas(pefaCount);

        return groupedPefas;
      }
      return {};
    };

    /**
     * Handles the onclick event generated when a pefa card is clicked.
     * @param event the onClick event generated when the pefa is clicked.
     */
    const handlePefaClicked = async (event: React.MouseEvent<HTMLDivElement>) => {
      //Shortcut the rootstore from props. This saves us calling props.rootstore every time.
      //We can just call rootstore directly instead.
      const { rootStore } = props;

      //Select and pre-load the PEFA from the state.
      const pefaId = event.currentTarget.id;
      const pefaIdInt = parseInt(pefaId);
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        selectedPefaId: pefaId,
      }));
      await rootStore?.pefaStore.selectPefa(pefaIdInt);
      let bookingNote = "";
      if (props.rootStore?.pefaStore.selectedPefa) {
        const pefaAssessment = props.rootStore?.pefaStore.selectedPefa[0];
        bookingNote = pefaAssessment.notes;
      }

      // OLD
      //If the PEFA is checked in and the current user is the assigned assessor,
      if (
        rootStore?.pefaStore.isPefaCheckedIn(pefaIdInt) &&
        rootStore?.pefaStore.isAssignedAssessor(pefaIdInt, rootStore.userStore.getUserAssessorNumber) &&
        rootStore.userStore.isUserAssessor
      ) {
        // check if there is a previous session
        const snapshots = await rootStore?.pefaStore.getSnapshots(pefaIdInt);
        //filter the unique pages
        if (snapshots != null && snapshots.length > 0 && !(snapshots.length === 1 && snapshots[0].last_Page === "")) {
          let lastPages = snapshots.map(function (obj: Snapshot) {
            return obj.last_Page;
          });
          lastPages = lastPages.filter(function (v: any, i: any) {
            return lastPages.indexOf(v) === i;
          });

          const menuPages: Snapshot[] = [];
          lastPages.forEach((element: any) => {
            const snapshot = snapshots.find((x: any) => x.last_Page === element);
            menuPages.push(snapshot);
          });
          setState((prevState) => ({
            ...prevState,
            menuPages: menuPages,
            selectedLastPage: menuPages[0].last_Page,
            isLoadLastPage: true,
          }));
        } else {
          //send them to the medical details page if the checkin includes informed consent.
          if (rootStore?.pefaStore.isCheckInIncludingInformedConsent(pefaIdInt)) {
            history.push(MEDICALDETAILS);
          } else {
            //Otherwise, send them to the informed consent page instead.
            history.push(INFORMEDCONSENT);
          }
        }
      } else {
        if (bookingNote !== null && bookingNote !== "") {
          setState((prevState) => ({
            ...prevState,
            bookingNote: bookingNote,
            isLoading: false,
            isLoadBookingNote: true,
          }));
        } else {
          // If they are not the assigned assessor and the pefa is not checked in, take them to the beginning.
          history.push(REFERRALDETAILS);
        }
      }
    };
    const handlePefaPreviewClicked = async (event: React.MouseEvent<HTMLDivElement>, pefaID: string) => {
      event.stopPropagation();
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      await props.rootStore?.pefaStore.selectPefa(parseInt(pefaID));
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
      history.push(ASSESSMENTPREVIEW);
    };

    const handlePefaListingChange = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      let { pefaListingCurrent } = state;

      //If there has been no change, escape the function and do nothing.
      if ((pefaListingCurrent && event.currentTarget.name === "currentPefas") || (!pefaListingCurrent && event.currentTarget.name === "completedPefas")) {
        return;
      }

      if (event.currentTarget.name === "currentPefas") {
        pefaListingCurrent = true;
      } else {
        pefaListingCurrent = false;
      }

      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        pefaListingCurrent,
      }));
    };

    const fetchPefaHandler = async (refreshPefas: boolean) => {
      if (auth) {
        const userInfo = auth.isLoading ? await auth.userManager.getUser() : auth.userData;

        if (userInfo?.access_token && refreshPefas) {
          await props.rootStore?.pefaStore.fetchPefas();
          const groupedPefas = filterPefas(state.periodKey);

          // update caches of checkin and informed consent
          const pefaKeys = Object.keys(groupedPefas);
          pefaKeys.forEach((pefaKey) => {
            const pefa = groupedPefas[pefaKey];
            if (pefa.length > 0) {
              if (pefa[0].checkinInformation != null) {
                props.rootStore?.dataStore.saveCheckInData(JSON.parse(pefa[0].checkinInformation.content), pefa[0].id, dataStorePageKeys.REFERRAL_DETAILS);
              }
              if (pefa[0].informedConsent != null) {
                props.rootStore?.dataStore.saveCheckInData(JSON.parse(pefa[0].informedConsent), pefa[0].id, dataStorePageKeys.INFORMED_CONSENT);
              }
            }
          });

          setState((prevState) => ({
            ...prevState,
            isLoading: false,
            pefaDateKeys: Object.keys(groupedPefas),
            groupedPefas,
          }));
        }
      }
    };
    const handleSelectLastPage = (event: any) => {
      event.persist();
      const snap = state.menuPages.find((x) => x.last_Page === event.target.value);
      if (snap != null) {
        setState((prevState) => ({
          ...prevState,
          selectedLastPage: event.target.value,
          selectedSysEndTime: snap?.sysEndTime,
        }));
      }
    };
    const handleNewPefa = async () => {
      await props.rootStore?.dataStore.clearSnapshotData(state.selectedPefaId);
      setState((prevState) => ({
        ...prevState,
        isLoadLastPage: false,
      }));
      history.push(REFERRALDETAILS);
    };
    const handleGoToPage = async () => {
      const latestSnapshot = await props.rootStore?.pefaStore.getSpecificSnapshot(parseInt(state.selectedPefaId), state.selectedSysEndTime);
      await props.rootStore?.dataStore.restoreSnapshotData(latestSnapshot, state.selectedPefaId);
      setState((prevState) => ({
        ...prevState,
        isLoadLastPage: false,
      }));
      history.push(state.selectedLastPage);
    };
    const handleCloseModal = (): void => {
      setState((prevState) => ({
        ...prevState,
        isLoadLastPage: false,
      }));
    };
    const handleCloseBookingNoteModal = (): void => {
      setState((prevState) => ({
        ...prevState,
        isLoadBookingNote: false,
      }));
      history.push(REFERRALDETAILS);
    };
    /**
     * Create a listener that will run only the first time the dashboard is loaded.
     */
    useEffect(() => {
      async function initDashboard() {
        const { rootStore } = props;

        if (!rootStore) return;

        if (!auth.isLoading) {
          await fetchPefaHandler(true);
        }

        setState((prevState) => ({
          ...prevState,
          isInitialised: true,
          isLoading: false,
          pefaCount: rootStore.pefaStore.pefaCount,
          name: !!rootStore.userStore.userDetails ? rootStore.userStore.getUserNameFormatted : "",
        }));
      }
      if (!state.isInitialised) {
        initDashboard();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.isLoading]);

    /**
     * Create a listener for the periodKey value stored in the state.
     * If the value changes, call the filterPefas function and update the pefas that are displayed.
     */
    useEffect(() => {
      const groupedPefas = filterPefas(state.periodKey);

      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        pefaDateKeys: Object.keys(groupedPefas),
        groupedPefas,
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.periodKey]);

    return (
      <div className="Dashboard">
        <Header />
        <Grid container>
          <Grid item xs={12}></Grid>
          <Grid item container>
            <Grid item xs={1} sm={1} />

            <Grid item container direction="column" xs={10} sm={10}>
              <Grid item container style={{ marginTop: "30px" }} direction="column" xs={12}>
                <Grid item xs={9}>
                  <Typography variant="h1" style={{ color: "#FFFFFF" }}>
                    <img alt="wave" src={Wave} className="wave" style={{ marginRight: "10px" }} />
                    Welcome to your <br /> schedule, <span style={{ color: "#1CBEAF" }}>{state.name}</span>
                  </Typography>
                </Grid>
                <Grid item xs={3} />
              </Grid>
              <Grid item container spacing={2} style={{ marginTop: "0px" }} direction="row" xs={12}>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className="introText">
                    <Button className="refreshButton" onClick={handleRefreshPefas}>
                      <img alt="refresh" src={RefreshIcon} style={{ padding: "0px 0px" }} width="45" />{" "}
                    </Button>{" "}
                    You have <span style={{ color: "#1CBEAF", fontWeight: "bold" }}>{props.rootStore?.pefaStore.getTotalNumberOfAppointments()}</span> PEFA's lined up.
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  {state.pefaListingCurrent ? (
                    <FormControl fullWidth={true} variant="outlined">
                      <Select color="primary" fullWidth={true} id="dayselector-select-outlined" onChange={handlePeriodChange} value={state.periodKey} className="DayChanger">
                        <MenuItem value={filteredPeriodKeys.TODAY}>Today</MenuItem>
                        <MenuItem value={filteredPeriodKeys.TOMORROW}>Tomorrow</MenuItem>
                        <MenuItem value={filteredPeriodKeys.THREEDAYS}>Next 3 days</MenuItem>
                        <MenuItem value={filteredPeriodKeys.SEVENDAYS}>Next 7 Days</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <></>
                  )}
                </Grid>
              </Grid>
              {!isChrome() && (
                <div
                  style={{
                    fontSize: "0.9em",
                    position: "relative",
                    padding: ".75rem 1.25rem",
                    border: "1px solid transparent",
                    borderRadius: ".25rem",
                    color: "#856404",
                    backgroundColor: "#fff3cd",
                    borderColor: "#ffeeba",
                    marginTop: "40px",
                  }}
                >
                  We recommend <strong>Google Chrome</strong> browser for best performance.
                </div>
              )}
              <Grid item container spacing={2} style={{ marginBottom: "10px" }} direction="row" xs={12}>
                <Grid item xs={5}>
                  <Typography className="pefaAppointments">
                    <span>{totalPefas}</span> PEFA Appointments
                  </Typography>
                </Grid>
                <Grid
                  item
                  container
                  xs={7}
                  direction="row"
                  justify="center"
                  alignItems="center"
                  style={{
                    textAlign: "right",
                    justifyContent: "flex-end",
                    verticalAlign: "middle",
                    paddingTop: "60px",
                    paddingBottom: "24px",
                  }}
                >
                  <Typography style={{ color: "#222E50" }}>
                    <FiberManualRecordIcon style={{ color: "#dde2e5", marginBottom: "-7px" }} /> Pending
                  </Typography>
                  <Typography style={{ color: "#222E50", marginLeft: "25px" }}>
                    <FiberManualRecordIcon style={{ color: "#FEC93D", marginBottom: "-7px" }} /> In Progress
                  </Typography>
                  <Typography style={{ color: "#222E50", marginLeft: "25px" }}>
                    <FiberManualRecordIcon style={{ color: "#20D347", marginBottom: "-7px" }} /> Checked In
                  </Typography>
                  <Typography style={{ color: "#222E50", marginLeft: "25px" }}>
                    <FiberManualRecordIcon style={{ color: "#FF0000", marginBottom: "-7px" }} /> Past
                  </Typography>
                </Grid>
              </Grid>
              <Grid item container direction="column">
                {state.pefaDateKeys.length > 0 ? (
                  <>
                    {state.pefaDateKeys.map((pefaDataKey: any) => {
                      if (state.groupedPefas[pefaDataKey].length > 0) {
                        return (
                          <>
                            <Typography className="daterK">{pefaDataKey}</Typography>
                            {state.groupedPefas[pefaDataKey].map((pefa: any) => (
                              <PefaCard key={pefa.id} pefaAssessment={pefa} handlePefaCardClick={handlePefaClicked} handlePefaPreviewClick={handlePefaPreviewClicked} />
                            ))}
                          </>
                        );
                      }
                    })}
                  </>
                ) : (
                  [
                    !state.isLoading ? (
                      <Card key="none" className="PefaCard" variant="outlined">
                        <Typography
                          style={{
                            margin: "25px",
                          }}
                        >
                          There are no PEFA's to display.
                        </Typography>
                      </Card>
                    ) : (
                      ""
                    ),
                  ]
                )}
              </Grid>
            </Grid>

            <Grid item xs={1} sm={1} />
          </Grid>
        </Grid>
        <Modal
          aria-labelledby="modal-title"
          open={state.isLoadLastPage}
          onClose={handleCloseModal}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Slide direction="up" in={state.isLoadLastPage} mountOnEnter unmountOnExit>
            <div className="modalPopLastPage">
              <Button className="hideBtn" onClick={handleCloseModal}>
                Close
              </Button>
              <div className="LoadLastPageSubmitModalText">
                <Typography variant="h2" className="notesHeader">
                  Previous Session Detected
                </Typography>
                <FormGroup className="formGroupOveride">
                  <Grid xs={12} item container className="StrengthTestContainer">
                    <FormControl component="fieldset" className="fieldsetStrength">
                      <Typography variant="caption" className="pastNotesHeader">
                        We've detected an existing session previously started. Please select where you want to continue from:
                      </Typography>
                      <FormGroup className="formGroupOveride">
                        <Select
                          name="last-page"
                          color="primary"
                          className="lastPages"
                          placeholder="Select"
                          value={state.selectedLastPage}
                          MenuProps={{ PaperProps: { style: { maxHeight: "18%" } } }}
                          onChange={(event) => {
                            handleSelectLastPage(event);
                          }}
                        >
                          {state.menuPages.map((value, index) => {
                            return (
                              <MenuItem value={value.last_Page} key={index}>
                                {dataStorePathKeysFriendly[value.last_Page]} ({value.sysEndTime ? value.timestamp : "Most Recent"})
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormGroup>
                      <div className="goto-last">
                        <Button onClick={handleNewPefa} color="primary" variant="outlined">
                          Start New PEFA
                        </Button>
                        <Button onClick={handleGoToPage} color="primary" variant="contained">
                          Restore Session
                        </Button>
                      </div>
                    </FormControl>
                  </Grid>
                </FormGroup>
              </div>
            </div>
          </Slide>
        </Modal>
        <Modal
          aria-labelledby="modal-title"
          open={state.isLoadBookingNote}
          onClose={handleCloseBookingNoteModal}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Slide direction="up" in={state.isLoadBookingNote} mountOnEnter unmountOnExit>
            <div className="modalPopBookingNote">
              <Button className="hideBtn" onClick={handleCloseBookingNoteModal}>
                Close
              </Button>
              <div className="LoadLastPageSubmitModalText">
                <Typography variant="h2" className="notesHeader">
                  Booking Notes
                </Typography>
                <Typography className={"modalBookingNote"} variant="body1">
                  {state.bookingNote}
                </Typography>
              </div>
            </div>
          </Slide>
        </Modal>
        <LoadingIndicator visible={state.isLoading} />
      </div>
    );
  })
);

export default Dashboard;
